<script setup lang="ts">
// Reusable interactive terminal pane — a full read-write xterm bound to one
// session over /ws/remote|/ws/nx (§10.8 binary framing). Used by the split
// multi-view; deliberately desktop-focused (no mobile keyboard/accessory bar,
// that lives in TerminalView). Emits connection status so the parent can show
// a dot per pane.
import { onBeforeUnmount, onMounted, ref, shallowRef } from "vue";
import { Terminal } from "xterm";
import { FitAddon } from "xterm-addon-fit";
import "xterm/css/xterm.css";
import { terminalOutput, terminalSocketUrl } from "@/terminalStream";
import { terminalClipboard } from "@/terminalClipboard";
import { useI18n } from "vue-i18n";
import { describeError } from "@/utils/errors";

const props = defineProps<{
  sessionId: string;
  targetType: "agent" | "nx";
}>();
const emit = defineEmits<{
  status: [value: "connecting" | "connected" | "closed"];
}>();
const { t: translate } = useI18n();
const imageMessage = ref("");
const imageError = ref(false);
let imageClipboard: ReturnType<typeof terminalClipboard> | null = null;

const FRAME_INPUT = 0x01;
const FRAME_RESIZE = 0x03;

const paneEl = ref<HTMLDivElement | null>(null);
const term = shallowRef<Terminal | null>(null);
const fit = shallowRef<FitAddon | null>(null);
let socket: WebSocket | null = null;
let output: ReturnType<typeof terminalOutput> | null = null;
let resizeObserver: ResizeObserver | null = null;

function xtermTheme() {
  const css = getComputedStyle(document.documentElement);
  const bg = css.getPropertyValue("--color-bg").trim() || "#0d1117";
  const fg = css.getPropertyValue("--color-fg").trim() || "#e6edf3";
  return { background: bg, foreground: fg, cursor: fg };
}

function refit() {
  requestAnimationFrame(() => {
    try {
      fit.value?.fit();
    } catch {
      /* layout not ready */
    }
  });
}

function sendResize() {
  const t = term.value;
  if (!t || !socket || socket.readyState !== WebSocket.OPEN) return;
  const frame = new Uint8Array(5);
  frame[0] = FRAME_RESIZE;
  const dv = new DataView(frame.buffer);
  dv.setUint16(1, t.cols, false);
  dv.setUint16(3, t.rows, false);
  socket.send(frame);
}

function sendInput(data: string) {
  if (!socket || socket.readyState !== WebSocket.OPEN || data.length === 0) return;
  const bytes = new TextEncoder().encode(data);
  const frame = new Uint8Array(1 + bytes.length);
  frame[0] = FRAME_INPUT;
  frame.set(bytes, 1);
  socket.send(frame);
}

function onThemeChange() {
  if (term.value) {
    term.value.options.theme = xtermTheme();
    refit();
  }
}

/** Focus the terminal — parent calls this when its pane is activated. */
function focus() {
  term.value?.focus();
}
defineExpose({ focus });

onMounted(() => {
  if (!paneEl.value) return;
  const t = new Terminal({
    convertEol: false,
    cursorBlink: true,
    fontSize: 12,
    scrollback: 3000,
    fontFamily: 'ui-monospace, "JetBrains Mono", "SF Mono", Menlo, Consolas, monospace',
    theme: xtermTheme(),
  });
  const fitAddon = new FitAddon();
  t.loadAddon(fitAddon);
  t.open(paneEl.value);
  fitAddon.fit();
  term.value = t;
  fit.value = fitAddon;
  if (props.targetType === "agent") {
    imageClipboard = terminalClipboard(
      paneEl.value,
      t,
      props.sessionId,
      () => socket?.readyState === WebSocket.OPEN,
      (uploading, error) => {
        imageError.value = !!error;
        imageMessage.value = error
          ? describeError(error, translate)
          : uploading
            ? translate("terminal.image_uploading")
            : "";
      },
    );
  }

  t.onData((data) => sendInput(data));
  t.onResize(() => sendResize());

  const wsPath = props.targetType === "nx" ? "/ws/nx" : "/ws/remote";
  const ws = new WebSocket(terminalSocketUrl(wsPath, props.sessionId));
  const decoder = terminalOutput(
    (bytes) => new Promise<void>((resolve) => t.write(bytes, resolve)),
    () => ws.close(),
    (frame) => {
      if (ws.readyState === WebSocket.OPEN) ws.send(frame);
    },
  );
  output = decoder;
  ws.binaryType = "arraybuffer";
  socket = ws;
  emit("status", "connecting");

  ws.onopen = () => {
    emit("status", "connected");
    refit();
    sendResize();
  };
  ws.onmessage = (ev) => {
    decoder.push(ev.data);
  };
  ws.onclose = async () => {
    await decoder.drain();
    if (!term.value) return;
    emit("status", "closed");
    socket = null;
    term.value?.writeln("\r\n\x1b[2m-- disconnected --\x1b[0m");
  };

  resizeObserver = new ResizeObserver(() => refit());
  resizeObserver.observe(paneEl.value);
  window.addEventListener("nyx-theme", onThemeChange);
});

onBeforeUnmount(() => {
  imageClipboard?.dispose();
  output?.dispose();
  window.removeEventListener("nyx-theme", onThemeChange);
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
  if (socket) {
    socket.close();
    socket = null;
  }
  if (term.value) {
    term.value.dispose();
    term.value = null;
  }
});
</script>

<template>
  <div ref="paneEl" class="tpane" @mousedown="focus">
    <p
      v-if="imageMessage"
      class="tpane__image"
      :class="{ 'admin-error': imageError }"
      :role="imageError ? 'alert' : 'status'"
    >
      {{ imageMessage }}
    </p>
  </div>
</template>

<style scoped>
.tpane {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  background: var(--color-bg);
  overflow: hidden;
  padding: 0.3rem;
}
.tpane__image {
  position: absolute;
  bottom: 0.25rem;
  left: 0.5rem;
  right: 0.5rem;
  z-index: 2;
  margin: 0;
  padding: 0.3rem;
  background: var(--color-bg-elev);
  font-size: 0.8rem;
}
</style>
